// Sugalarm — Alarm Clock Mode (cycle 145; v2 layout cycle 149).
//
// The bedside idle face. Cycle-149 v2: when insulin is on board the
// REUSED collapsed `BolusRings` (cycle 147/148 — no ghost tracks)
// occupy the left third, rings + center readout only (no
// MealContextLines — the face stays glanceable); the clock is the
// right-side hero with the glucose readout underneath, day + date on
// ONE line above. With nothing on board the rings column is omitted
// and clock + glucose center across the full display. Both heroes stay
// colored from the live threshold zone (neutral grey when stale).
// PURELY PRESENTATIONAL — renders below the alarm banner row, after
// the urgent early-return; owns no alarm logic. While active, the App
// appearance applier forces the dark/midnight theme (reversible, never
// writes display_theme).

import { useEffect, useRef } from "react";

import { BolusRings } from "../../components/glucose/BolusRings";
import { ClockFaceWaves } from "../../components/kiosk/ClockFaceWaves";
import { PortraitClockFace } from "../../components/kiosk/PortraitClockFace";
import { TrendArrow } from "../../components/glucose/TrendArrow";
import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { useIsLandscape } from "../../hooks/useIsLandscape";
import { useNow } from "../../hooks/useNow";
import { useSettings } from "../../hooks/useSettings";
import { useThresholds } from "../../hooks/useThresholds";
import { hapticSelect } from "../../lib/haptics";
import type { BasalContext, BolusForRings } from "../../lib/iob";
import { selectCurrentGlucoseFor } from "../../lib/currentGlucose";
import {
  displayValue,
  fmtAge,
  recentDeltaMgdl,
  toMmol,
  trendInfo,
  zoneColor,
  zoneLabel,
  zoneOf,
} from "../../lib/glucose";
import styles from "./AlarmClockMode.module.css";

interface Props {
  socket: GlucoseSocketState;
  variant: FrameVariant;
  units: "mg/dL" | "mmol/L";
  /** Whole-face night dim (derived by the controller — already false
   *  whenever an alarm episode is live). */
  dim: boolean;
  /** Cycle 149: the same colored ring set + basal context the Now
   *  screen computed — feeds the reused BolusRings on the face. */
  boluses: BolusForRings[];
  basal: BasalContext | null;
  /** Cycle 149.1: the active member's display name, centered bold white
   *  at the top of the face. */
  memberName?: string | null;
  /** Cycle 167: a collapsed Low/High alarm strip is pinned to the
   *  bottom edge (the banners render it fixed while clock mode is
   *  active) — reserve its height in the face layout so it never
   *  overlaps the heroes; reclaimed when no strip is mounted. */
  reserveBottomStrip?: boolean;
  /** Cycle 452.2: off the kiosk the collapsed URGENT strip stays in flow at
   *  the TOP of the screen, over this full-bleed face — reserve its band
   *  there so the member's name and the Dim control are never under it. */
  reserveTopStrip?: boolean;
  onExit: () => void;
  onToggleDim: () => void;
}

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function AlarmClockMode({
  socket,
  variant,
  units,
  dim,
  boluses,
  basal,
  memberName = null,
  reserveBottomStrip = false,
  reserveTopStrip = false,
  onExit,
  onToggleDim,
}: Props) {
  const thresholds = useThresholds();
  const { settings } = useSettings();
  const h24 = settings?.display_24h ?? false;
  const now = useNow(1000); // second ticks drive the clock
  const isLandscape = useIsLandscape();

  // Cycle 433: the clock face follows the same display rule.
  // ⚠ Cycle 435: through the ONE selector, which samples `Date.now()` for
  // itself — never the ticking `now` above, which a suspended webview
  // freezes along with the number.
  const view = selectCurrentGlucoseFor(socket, socket.activeMemberId);
  const current = view.displayValue;
  const stale = current == null;
  const noData = stale;
  const zone = zoneOf(current?.sgv, thresholds);
  const color = noData ? "var(--ink-3)" : zoneColor(zone);
  const trend = trendInfo(current?.trend ?? null, current?.trendRate ?? null);
  const ageMin = view.ageMinutes ?? 0;
  // Cycle 435: history maths in the lib; painted only beside a current
  // number (`deltaStr` renders under the `!noData` branch).
  const deltaMgdl = recentDeltaMgdl(socket.recentReadings);
  const deltaStr =
    deltaMgdl !== null
      ? `${deltaMgdl >= 0 ? "+" : ""}${
          units === "mmol/L" ? toMmol(deltaMgdl).toFixed(1) : deltaMgdl
        }`
      : null;

  // Cycle 166: the bedside-face ring toggle (additive settings key;
  // absent = true → rings shown, today's behavior). The face is the
  // ONLY consumer — every other ring surface ignores it. Three explicit
  // layouts: rings OFF → the symmetric glucose-left / clock-right
  // split (regardless of insulin on board); rings ON + insulin on
  // board → the cycle-149 rings-left layout, unchanged; rings ON +
  // nothing on board → the centered layout, unchanged.
  const showIobRings = settings?.alarm_clock?.show_iob_rings ?? true;

  // Cycle 149: the rings column shows only when insulin is actually on
  // board (rapid or basal) — never the dimmed empty-rings state.
  // Cycle 198: this is now the ONLY layout discriminant. The lower block
  // under the clock is the IOB ring beside the glucose number when
  // insulin is on board; otherwise the glucose block alone (rings off,
  // or rings on with nothing on board) — one vertical centered stack
  // either way (the cycle-167 horizontal split is gone).
  const showRings = showIobRings && (boluses.length > 0 || basal != null);

  // Clock pieces.
  const d = new Date(now);
  const rawH = d.getHours();
  const hh = h24 ? rawH : rawH % 12 === 0 ? 12 : rawH % 12;
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ampm = rawH < 12 ? "AM" : "PM";
  const dateLine = `${WEEKDAYS[d.getDay()]} · ${MONTHS[d.getMonth()]} ${d.getDate()}`;

  // Cycle 145 deliverable 7, HARDENED cycle 166 — mobile keep-awake, web
  // APIs only (no Capacitor plugin — reliable native iOS keep-awake stays
  // a flagged native fast-follow). Cycle 348 removed the best-effort
  // `screen.orientation.lock`/`unlock` calls this effect used to also
  // make — see the removal note further down in this same effect body.
  //
  // The cycle-145 single wakeLock acquisition dropped in practice: some
  // engines auto-release the lock (their own `release` event), and a
  // background/foreground round trip or a rotation could leave the
  // session lockless. Now: (1) re-acquire on the lock's own `release`
  // event, on visibilitychange→visible, on window focus, and on
  // orientationchange; (2) a denied/thrown request RETRIES on a short
  // timer instead of giving up; (3) engines with no working Screen Wake
  // Lock (notably iOS WKWebView) fall back to a hidden, MUTED,
  // playsinline, looping 1-second video (web/public/keepawake.mp4,
  // ~1.6 KB) that defeats the idle timer — mounted only on the
  // fallback path and only while clock mode is active, torn down on
  // exit, and silent by construction so it can never touch the alarm
  // WAVs. Kiosk gets none of this (the Chromium appliance never
  // sleeps).
  const wakeLockRef = useRef<{ release: () => Promise<void> } | null>(null);
  useEffect(() => {
    if (variant !== "mobile") return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let fallbackVideo: HTMLVideoElement | null = null;
    type WakeLockSentinel = {
      release: () => Promise<void>;
      addEventListener?: (t: "release", l: () => void) => void;
    };
    const nav = navigator as unknown as {
      wakeLock?: {
        request: (t: "screen") => Promise<WakeLockSentinel>;
      };
    };

    const startFallbackVideo = () => {
      if (cancelled || fallbackVideo) return;
      const v = document.createElement("video");
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute("playsinline", "");
      v.setAttribute("muted", "");
      v.setAttribute("aria-hidden", "true");
      v.src = "/keepawake.mp4";
      // Present-but-invisible: fully transparent/clipped video elements
      // can be ignored by the idle heuristic, so keep a real (1px,
      // offscreen) box rather than display:none.
      v.style.cssText =
        "position:fixed;left:-9999px;bottom:0;width:1px;height:1px;pointer-events:none;";
      document.body.appendChild(v);
      fallbackVideo = v;
      void v.play().catch(() => {
        /* autoplay denied — nothing more we can do without a gesture;
           the next acquire() pass retries play() below. */
      });
    };
    const stopFallbackVideo = () => {
      if (!fallbackVideo) return;
      fallbackVideo.pause();
      fallbackVideo.removeAttribute("src");
      fallbackVideo.remove();
      fallbackVideo = null;
    };

    const acquire = async () => {
      if (cancelled) return;
      // No Screen Wake Lock at all → straight to the video fallback.
      if (!nav.wakeLock) {
        startFallbackVideo();
        return;
      }
      try {
        const lock = await nav.wakeLock.request("screen");
        if (cancelled) {
          void lock.release().catch(() => undefined);
          return;
        }
        wakeLockRef.current = lock;
        // The real lock is held — the fallback (from an earlier failed
        // pass) is redundant.
        stopFallbackVideo();
        // Some engines auto-drop the lock; re-acquire the moment the
        // sentinel reports its own release (skip while hidden — the
        // visibilitychange handler re-acquires on return).
        lock.addEventListener?.("release", () => {
          if (wakeLockRef.current === lock) wakeLockRef.current = null;
          if (!cancelled && document.visibilityState === "visible") {
            void acquire();
          }
        });
      } catch {
        // Denied/failed (iOS WKWebView rejects): fall back to the muted
        // video AND keep retrying the real lock on a timer.
        startFallbackVideo();
        if (fallbackVideo) void fallbackVideo.play().catch(() => undefined);
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = setTimeout(() => {
          retryTimer = null;
          void acquire();
        }, 15_000);
      }
    };
    void acquire();

    const reacquire = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      if (!wakeLockRef.current) void acquire();
      // A backgrounded tab pauses media; nudge the fallback on return.
      if (fallbackVideo) void fallbackVideo.play().catch(() => undefined);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") reacquire();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", reacquire);
    window.addEventListener("orientationchange", reacquire);
    // Cycle 348: the best-effort `screen.orientation.lock("landscape")` +
    // matching `unlock()` that used to live here are REMOVED — orientation
    // is now owned entirely by the Clock controller (useAlarmClockMode.ts),
    // which calls the real native `@capacitor/screen-orientation` plugin
    // (lib/orientationLock.ts) to allow free rotation the instant clock
    // mode goes active and re-lock to portrait the instant it exits. This
    // web-API call predated PortraitClockFace (a real portrait bedside
    // layout didn't exist yet, so forcing landscape was the only usable
    // face) and had nothing to do with keeping the screen awake — only the
    // wakeLock/keep-awake logic in this effect is unrelated and unchanged.
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", reacquire);
      window.removeEventListener("orientationchange", reacquire);
      stopFallbackVideo();
      const lock = wakeLockRef.current;
      wakeLockRef.current = null;
      if (lock) void lock.release().catch(() => undefined);
    };
  }, [variant]);

  // Cycle 149: the prominent exit control, shared by the face and the
  // portrait rotate prompt.
  const exitButton = (
    <button
      type="button"
      className={styles.exitBtn}
      aria-label="Exit clock mode"
      onClick={() => {
        hapticSelect();
        onExit();
      }}
    >
      <ExitGlyph />
    </button>
  );

  // The 44px dim toggle — shared by the face and (cycle 157) the
  // portrait rotate prompt, so both surfaces carry the same controls.
  const dimButton = (
    <button
      type="button"
      className={`${styles.cornerBtn} ${styles.cornerBtnRight}`}
      aria-label={dim ? "Brighten" : "Dim"}
      aria-pressed={dim}
      onClick={() => {
        hapticSelect();
        onToggleDim();
      }}
    >
      <MoonGlyph />
    </button>
  );

  // Cycle 167: the face ground is full-bleed to BOTH screen edges on
  // every surface. Kiosk/web have been since cycle 145.1 (absolute /
  // fixed, inset 0); MOBILE was the one surface still rendered in-flow
  // — it sat below MobileFrame's `env(safe-area-inset-top)` root
  // padding and any in-flow siblings, letting the frame ground (the
  // cycle-164 aurora, distinct from the face's forced-midnight ground)
  // peek through as a strip above the face. `.mobileFull` pins the
  // mobile face fixed like the web face (same class of fix as 145.1's
  // bottom). `.stripReserve` reserves the bottom alarm-strip band only
  // while a collapsed Low/High strip is mounted.
  const variantClass =
    variant === "kiosk"
      ? styles.kiosk
      : variant === "web"
        ? styles.web
        : styles.mobileFull;
  const reserveClass = `${reserveBottomStrip ? styles.stripReserve : ""} ${
    reserveTopStrip ? styles.stripReserveTop : ""
  }`;

  // Mobile portrait: cycle 253 replaces the cycle-157 rotate-to-landscape
  // prompt with a real vertical clock face (glucose top, zone-colored
  // rounded clock centre, IOB rings bottom, animated wave background). It
  // reuses the same computed zone color / staleness / clock pieces this
  // component already derived, so the portrait clock tracks the threshold
  // zone exactly like the landscape face. The landscape branch below is
  // byte-identical.
  if (variant === "mobile" && !isLandscape) {
    return (
      <PortraitClockFace
        color={color}
        zone={zone}
        noData={noData}
        stale={stale}
        current={current}
        units={units}
        trend={trend}
        deltaStr={deltaStr}
        ageMin={ageMin}
        thresholds={thresholds}
        hh={hh}
        mm={mm}
        ss={ss}
        ampm={ampm}
        h24={h24}
        dateLine={dateLine}
        dim={dim}
        showRings={showRings}
        boluses={boluses}
        basal={basal}
        now={now}
        reserveBottomStrip={reserveBottomStrip}
        reserveTopStrip={reserveTopStrip}
        onExit={onExit}
      />
    );
  }

  // The glucose readout block — under the clock (cycle-149 v2).
  const glucoseBlock = (
    <div className={styles.glucoseBlock}>
      <div className={styles.eyebrow} style={{ color }}>
        <span className={styles.eyebrowDot} style={{ background: color }} />
        {noData ? "No data" : zoneLabel(zone)}
      </div>
      <div className={styles.glucoseRow}>
        <span className={`${styles.glucoseNum} tnum`} style={{ color }}>
          {noData || !current ? "—" : displayValue(current.sgv, units)}
        </span>
        {!noData && (
          <TrendArrow
            glyph={trend.glyph}
            zone={zone}
            stale={stale}
            size="clamp(22px, 3vw, 36px)"
          />
        )}
      </div>
      <div className={styles.details}>
        {noData ? (
          <div className={styles.detailLine}>Please check the CGM.</div>
        ) : (
          <div className={`${styles.detailLine} tnum`}>
            {trend.label}
            {deltaStr !== null ? ` · ${deltaStr} ${units}` : ""}
            {current ? ` · ${fmtAge(ageMin)}` : ""}
          </div>
        )}
        <div className={styles.targetPill}>
          Target{" "}
          <span className="tnum">
            {displayValue(thresholds.target_low, units)}–
            {displayValue(thresholds.target_high, units)}
          </span>
        </div>
      </div>
    </div>
  );

  // ─── Cycle 198: ONE vertical centered stack for every layout ────────
  // Clock hero on TOP (HH:MM:SS, all digits one size, AM/PM the small
  // accent), the lower block directly underneath, everything center-
  // aligned. The lower block is the IOB triple-ring BESIDE the glucose
  // number when insulin is on board (`showRings`); otherwise the glucose
  // block alone (rings off, or rings on with nothing on board). Replaces
  // the cycle-167 horizontal glucose/clock split and the cycle-149
  // rings-left split. The clock keeps the threshold-zone color (the same
  // `color` the glucose number uses — neutral grey when stale).
  return (
    <div
      className={`${styles.root} ${styles.stack} ${
        dim ? styles.dimmed : ""
      } ${variantClass} ${reserveClass}`}
    >
      {/* Cycle 265: the same calm left-flowing wave background the portrait
          face uses, behind the content (z-index:-1, never over the heroes
          or any alarm surface). */}
      <ClockFaceWaves className={styles.canvas} />
      {exitButton}
      {dimButton}
      {memberName ? (
        <div className={styles.nameBanner}>{memberName}</div>
      ) : null}

      {/* Clock hero on top. Cycle 198: seconds are full-size (HH:MM:SS
          one size); AM/PM is the only small accent and drops in 24-hour
          mode (so the 24h face reads HH:MM:SS with no accent). */}
      <div className={styles.clockBlock}>
        <div className={styles.dateLine}>{dateLine}</div>
        <div className={styles.clockRow}>
          <span className={`${styles.clockNum} tnum`} style={{ color }}>
            {hh}:{mm}:{ss}
          </span>
          {!h24 && (
            <span className={styles.clockAccent} style={{ color }}>
              {ampm}
            </span>
          )}
        </div>
      </div>

      {/* Lower block, centered under the clock. Cycle 149: the reused
          collapsed BolusRings (rings + center readout only; hideContext
          drops the dose-lines column; cycle-150 `bare` drops the card
          panel) sit BESIDE the glucose number as one centered unit when
          insulin is on board; otherwise the glucose block alone. */}
      {showRings ? (
        <div className={styles.lowerRow}>
          <div className={styles.ringsCol}>
            <BolusRings
              boluses={boluses}
              now={now}
              basal={basal}
              hideContext
              bare
            />
          </div>
          {glucoseBlock}
        </div>
      ) : (
        glucoseBlock
      )}
    </div>
  );
}


function ExitGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4M10 17l-5-5 5-5M5 12h11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoonGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
